import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { translate } from "../i18n/hooks.ts";
import { readSavedLanguage } from "../model/language.ts";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Safety net for any exception that no local try/catch has caught
 * (confirmed: there was no ErrorBoundary anywhere in the app until now) —
 * without this, React unmounts the whole tree and leaves a blank screen
 * with no message at all. Doesn't replace the local try/catches that can
 * still show the loaded project and warn via the bell (see `calculationError`
 * in App.tsx) — this is the last resort for whatever none of them cover.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      // No LanguageProvider above (if the whole App.tsx crashed, React
      // unmounted that Provider too) — the saved language is read directly.
      const language = readSavedLanguage();
      return (
        <div className="fatal-error-screen">
          <div className="fatal-error-screen-card">
            <h1>{translate(language, "errorBoundary.title")}</h1>
            <p>{translate(language, "errorBoundary.message")}</p>
            <pre>{this.state.error.message}</pre>
            <button className="button button-primary" onClick={() => window.location.reload()}>
              {translate(language, "errorBoundary.reload")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
